import React, { createContext, useContext, useState, useEffect } from 'react';

const translations = {
  en: {
    nav: {
      home: 'Home',
      catalog: 'Villas',
      contact: 'Contact',
      admin: 'Admin',
    },
    hero: {
      subtitle: 'Luxury Seafront Villas in Croatia',
      title: 'Luxury Seafront Villas on the Croatian Coast',
      description: 'Discover a private collection of seafront villas in Dalmatia. Each property is offered directly by the developer, with a transparent purchase process. Exclusive waterfront homes across Dalmatia.',
      cta: 'View Available Villas',
      ctaSecondary: 'Contact Us on WhatsApp',
      imageAlt: 'Luxury villa with infinity pool at sunset in Croatia',
    },
    images: {
      aboutAlt: 'Seafront villas in Dalmatia, Croatia',
      videoAlt: 'Luxury villas in Dalmatia, Croatia',
      dalmatiaAlt: 'Dalmatian coastline at sunset',
      omisLabel: 'Omiš, Croatia',
    },
    about: {
      label: 'About Us',
      title: 'Private Waterfront Living in Dalmatia',
      description: 'For buyers seeking privacy, design quality and direct waterfront access, our collection offers a rare opportunity on the Croatian coast. Our villas are set in rare seafront locations across Dalmatia, blending contemporary architecture with the prestige of a prime coastal setting. All properties are offered directly by the developer. Each residence combines architectural clarity, privacy and a prime waterfront setting with long-term asset appeal.',
      details: 'Clean lines, expansive glazing and elegant open-plan interiors frame uninterrupted sea views. Floor-to-ceiling glass fills each space with natural light, creating a seamless connection between refined living and the beauty of the waterfront.',
      ctaButton: 'Request Details on WhatsApp',
      feature1: 'Best Offers',
      feature2: 'Confirmed Quality',
      feature3: 'Legal Support',
    },
    featuresSection: {
      title: 'Why Buyers Choose Villa.ooo',
    },
    villas: {
      label: 'Available Properties',
      title: 'Selected Seafront Villas in Croatia',
      viewAll: 'View Full Collection',
      bedrooms: 'bedrooms',
      bathrooms: 'bathrooms',
      area: 'm²',
      details: 'Details',
      noVillas: 'Villas coming soon',
    },
    contact: {
      label: 'Contact',
      title: 'Get in Touch',
      description: 'Want to buy a villa in Croatia? Have questions about our houses for sale? We will gladly help!',
      name: 'Your Name',
      email: 'Email',
      phone: 'Phone',
      message: 'Message',
      send: 'Send Request',
      success: 'Request Sent!',
      successDesc: 'We will contact you shortly',
    },
    footer: {
      rights: 'All rights reserved',
      privacy: 'Privacy Policy',
      seoText: 'Private seafront villas in Dalmatia for international buyers seeking design, discretion and direct waterfront access.',
    },
    features: {
      bestOffers: 'Prime Waterfront Locations',
      bestOffersDesc: 'Our villas are designed with premium materials, contemporary engineering and a strong focus on energy performance.',
      quality: 'Verified Build Quality',
      qualityDesc: 'Complete project documentation is available to support transparency and buyer confidence.',
      saveMoney: 'Direct Developer Purchase',
      saveMoneyDesc: 'A direct purchase structure helps ensure pricing clarity and a transparent acquisition process.',
      bestPrice: 'Selected Villas in Prime Coastal Settings',
      bestPriceDesc: 'All villas enjoy a true frontline seafront position.',
      legalSupport: 'Legal and Transaction Guidance',
      legalSupportDesc: 'We support buyers with a transparent process and professional legal coordination throughout the transaction.',
      businessGain: 'Rental and Investment Potential',
      businessGainDesc: 'These properties may also appeal to buyers seeking premium short-term rental potential and long-term value retention in rare coastal locations.',
    },
    catalog: {
      title: 'Villa Catalog',
      subtitle: 'Find your dream villa',
      filter: 'Filters',
      sort: 'Sort',
      priceAsc: 'Price: Low to High',
      priceDesc: 'Price: High to Low',
      all: 'All Villas',
      properties: 'properties',
    },
    villa: {
      features: 'Features',
      location: 'Location',
      request: 'Request Info',
      gallery: 'Gallery',
      back: 'Back to Catalog',
      bedrooms: 'Bedrooms',
      bathrooms: 'Bathrooms',
      area: 'Living Area',
      land: 'Land Area',
      status: 'Status',
      forSale: 'For Sale',
      sold: 'Sold',
      reserved: 'Reserved',
      priceOnRequest: 'Price on request',
      comingSoon: 'Coming Soon',
      viewAllPhotos: 'View all photos',
      videoPresentation: 'Video Presentation',
      notFound: 'Villa not found',
    },
    admin: {
      login: 'Admin Login',
      email: 'Email',
      password: 'Password',
      loginBtn: 'Login',
      logout: 'Logout',
      dashboard: 'Dashboard',
      villas: 'Villas',
      contacts: 'Inquiries',
      addVilla: 'Add Villa',
      editVilla: 'Edit Villa',
      deleteVilla: 'Delete Villa',
      save: 'Save',
      cancel: 'Cancel',
      published: 'Published',
      featured: 'Featured',
      noContacts: 'No inquiries',
      markRead: 'Mark as Read',
    },
    seo: {
      homeTitle: 'Buy Seafront Villas in Croatia | Luxury Villas in Dalmatia',
      homeDescription: 'Buy luxury seafront villas in Croatia. Premium villas for sale in Dalmatia directly from the developer, with no hidden fees.',
      catalogTitle: 'Seafront Villas for Sale in Croatia — Villa.ooo',
      catalogDescription: 'Luxury seafront villas for sale in Dalmatia region, Croatia. Trogir, Primosten, Omis, Makarska. Direct from developer.',
    },
    dalmatia: {
      title: 'Dalmatia — Croatia\'s Most Sought-After Coastal Region',
      description: 'Dalmatia combines dramatic coastline, historic towns and some of the most desirable waterfront property locations in the Adriatic.',
      region: 'Region',
      fullDescription: 'Stretching along southern Croatia\'s Adriatic coast, Dalmatia is known for its clear waters, island landscapes, marinas and heritage towns. Its combination of scenery, infrastructure and lifestyle continues to attract international property buyers.',
    },
    video: {
      title: 'Video Presentation',
      subtitle: 'Explore Our Seafront Villas',
      description: 'Explore selected villas across Dalmatia, defined by contemporary architecture, privacy and panoramic Adriatic views.',
    },
    cta: {
      title: 'Request a Private Introduction to Our Villas',
      description: 'Browse the current collection or speak with our team for a private introduction and tailored guidance.',
      button: 'Browse Villas',
      whatsapp: 'Speak on WhatsApp',
      contact: 'Request Details',
    },
  },
  de: {
    nav: {
      home: 'Startseite',
      catalog: 'Villen',
      contact: 'Kontakt',
      admin: 'Admin',
    },
    hero: {
      subtitle: 'Luxusvillen zu verkaufen in Kroatien',
      title: 'Villa in Kroatien kaufen — Direkt am Meer',
      description: 'Möchten Sie eine Strandvilla in Kroatien kaufen? Wir bieten Luxusvillen zu verkaufen direkt vom Bauträger — keine versteckten Gebühren. Häuser zu verkaufen in Dalmatien.',
      cta: 'Villen ansehen',
      ctaSecondary: 'WhatsApp',
      imageAlt: 'Luxusvilla mit Infinity-Pool bei Sonnenuntergang in Kroatien',
    },
    images: {
      aboutAlt: 'Villen am Meer in Dalmatien, Kroatien',
      videoAlt: 'Luxusvillen in Dalmatien, Kroatien',
      dalmatiaAlt: 'Dalmatinische Küste bei Sonnenuntergang',
      omisLabel: 'Omiš, Kroatien',
    },
    about: {
      label: 'Über uns',
      title: 'Villa in Kroatien kaufen — Ihr Traumhaus wartet',
      description: 'Suchen Sie Häuser zu verkaufen in Kroatien? Unsere Luxusvillen zu verkaufen befinden sich direkt am Meer. Villa in Kroatien kaufen direkt vom Bauträger. Strandvillen mit außergewöhnlichem Lebensstil und starkem Investitionspotenzial.',
      details: 'Klare Linien, großzügige Verglasung und elegante offene Innenräume rahmen einen ununterbrochenen Meerblick. Raumhohe Glasflächen füllen jeden Raum mit natürlichem Licht und schaffen eine nahtlose Verbindung zwischen raffiniertem Wohnen und der Schönheit des Wassers.',
      ctaButton: 'Details per WhatsApp anfordern',
      feature1: 'Beste Angebote',
      feature2: 'Bestätigte Qualität',
      feature3: 'Rechtliche Unterstützung',
    },
    featuresSection: {
      title: 'Warum Käufer Villa.ooo wählen',
    },
    villas: {
      label: 'Villen zu verkaufen',
      title: 'Luxusvillen zu verkaufen in Kroatien',
      viewAll: 'Alle ansehen',
      bedrooms: 'Schlafzimmer',
      bathrooms: 'Badezimmer',
      area: 'm²',
      details: 'Details',
      noVillas: 'Villen kommen bald',
    },
    contact: {
      label: 'Kontakt',
      title: 'Kontaktieren Sie uns',
      description: 'Möchten Sie eine Villa in Kroatien kaufen? Wir helfen Ihnen gerne!',
      name: 'Ihr Name',
      email: 'E-Mail',
      phone: 'Telefon',
      message: 'Nachricht',
      send: 'Anfrage senden',
      success: 'Anfrage gesendet!',
      successDesc: 'Wir werden Sie in Kürze kontaktieren',
    },
    footer: {
      rights: 'Alle Rechte vorbehalten',
      privacy: 'Datenschutz',
      seoText: 'Villa in Kroatien kaufen | Villen zu verkaufen in Kroatien | Luxus-Strandimmobilien | Häuser zu verkaufen in Dalmatien',
    },
    features: {
      bestOffers: 'Beste Angebote',
      bestOffersDesc: 'Wir bieten Villen bester Qualität aus natürlichen Materialien mit höchster Energieeffizienz',
      quality: 'Bestätigte Qualität',
      qualityDesc: 'Sie erhalten vollständige Projektdokumentation für volles Vertrauen in die Qualität',
      saveMoney: 'Geld sparen',
      saveMoneyDesc: 'Villa in Kroatien kaufen direkt vom Bauträger zu einem fairen Preis ohne versteckte Gebühren',
      bestPrice: 'Beste Villen zum besten Preis',
      bestPriceDesc: 'Suchen Sie Luxusvillen zu verkaufen in Kroatien? Alle unsere Villen liegen in erster Meereslinie',
      legalSupport: 'Rechtliche Unterstützung',
      legalSupportDesc: 'Wir garantieren absolute Transparenz und volle Einhaltung der EU-Vorschriften',
      businessGain: 'Geschäftsgewinn',
      businessGainDesc: 'Die Villa kann vermietet werden und hohe Einnahmen erzielen',
    },
    catalog: {
      title: 'Villenkatalog',
      subtitle: 'Finden Sie Ihre Traumvilla',
      filter: 'Filter',
      sort: 'Sortieren',
      priceAsc: 'Preis: Niedrig bis Hoch',
      priceDesc: 'Preis: Hoch bis Niedrig',
      all: 'Alle Villen',
      properties: 'Objekte',
    },
    villa: {
      features: 'Ausstattung',
      location: 'Standort',
      request: 'Anfrage stellen',
      gallery: 'Galerie',
      back: 'Zurück zum Katalog',
      bedrooms: 'Schlafzimmer',
      bathrooms: 'Badezimmer',
      area: 'Wohnfläche',
      land: 'Grundstück',
      status: 'Status',
      forSale: 'Zu verkaufen',
      sold: 'Verkauft',
      reserved: 'Reserviert',
      priceOnRequest: 'Preis auf Anfrage',
      comingSoon: 'Demnächst',
      viewAllPhotos: 'Alle Fotos ansehen',
      videoPresentation: 'Videopräsentation',
      notFound: 'Villa nicht gefunden',
    },
    admin: {
      login: 'Admin-Anmeldung',
      email: 'E-Mail',
      password: 'Passwort',
      loginBtn: 'Anmelden',
      logout: 'Abmelden',
      dashboard: 'Dashboard',
      villas: 'Villen',
      contacts: 'Anfragen',
      addVilla: 'Villa hinzufügen',
      editVilla: 'Villa bearbeiten',
      deleteVilla: 'Villa löschen',
      save: 'Speichern',
      cancel: 'Abbrechen',
      published: 'Veröffentlicht',
      featured: 'Hervorgehoben',
      noContacts: 'Keine Anfragen',
      markRead: 'Als gelesen markieren',
    },
    seo: {
      homeTitle: 'Villa.ooo — Premium-Villen in erster Meereslinie in Dalmatien, Kroatien',
      homeDescription: 'Premium-Villen in Dalmatien, Kroatien. Villen in erster Meereslinie. Direkt vom Bauträger.',
      catalogTitle: 'Villen zum Verkauf in Kroatien — Villa.ooo',
      catalogDescription: 'Luxusvillen zum Verkauf in Dalmatien, Kroatien. Trogir, Primosten, Omis, Makarska.',
    },
    dalmatia: {
      title: 'Dalmatien — Die Perle Kroatiens',
      description: 'Entfliehen Sie an die bezaubernde dalmatinische Küste Kroatiens — ein verborgenes Juwel Europas.',
      region: 'Region',
      fullDescription: 'Dalmatien ist eine Region im südlichen Teil Kroatiens, die sich entlang der malerischen Küste der Adria erstreckt. Bekannt für seine atemberaubende natürliche Schönheit, bietet Dalmatien eine einzigartige Kombination aus Inseln, kristallklarem Wasser und reichem kulturellen Erbe.',
    },
    video: {
      title: 'Videopräsentation',
      subtitle: 'Unsere Villen ansehen',
      description: 'Entdecken Sie die schönsten Villen der dalmatinischen Inseln — architektonische Meisterwerke mit Panoramablick auf die Adria.',
    },
    cta: {
      title: 'Bereit, Ihre Traumvilla zu besitzen?',
      description: 'Durchsuchen Sie unseren exklusiven Villenkatalog oder kontaktieren Sie uns über WhatsApp für persönliche Beratung.',
      button: 'Villen ansehen',
      whatsapp: 'Per WhatsApp sprechen',
      contact: 'Details anfordern',
    },
  },
  pl: {
    nav: {
      home: 'Strona główna',
      catalog: 'Wille',
      contact: 'Kontakt',
      admin: 'Admin',
    },
    hero: {
      subtitle: 'Luksusowe wille na sprzedaż w Chorwacji',
      title: 'Kupić willę w Chorwacji — Nad samym morzem',
      description: 'Chcesz kupić willę nad morzem w Chorwacji? Oferujemy luksusowe wille na sprzedaż bezpośrednio od dewelopera — bez ukrytych opłat. Domy na sprzedaż w Dalmacji.',
      cta: 'Zobacz wille na sprzedaż',
      ctaSecondary: 'WhatsApp',
      imageAlt: 'Luksusowa willa z basenem infinity o zachodzie słońca w Chorwacji',
    },
    images: {
      aboutAlt: 'Wille nad morzem w Dalmacji, Chorwacja',
      videoAlt: 'Luksusowe wille w Dalmacji, Chorwacja',
      dalmatiaAlt: 'Wybrzeże Dalmacji o zachodzie słońca',
      omisLabel: 'Omiš, Chorwacja',
    },
    about: {
      label: 'O nas',
      title: 'Kupić willę w Chorwacji — Twój wymarzony dom czeka',
      description: 'Szukasz domów na sprzedaż w Chorwacji? Nasze luksusowe wille na sprzedaż znajdują się tuż przy morzu. Kup willę nad morzem w Chorwacji bezpośrednio od dewelopera. Wille z wyjątkowym stylem życia i silnym potencjałem inwestycyjnym.',
      details: 'Czyste linie, obszerne przeszklenia i eleganckie otwarte wnętrza tworzą ramy dla niezakłóconego widoku na morze. Szklane ściany od podłogi do sufitu wypełniają każdą przestrzeń naturalnym światłem, tworząc płynne połączenie między wyrafinowanym życiem a pięknem wybrzeża.',
      ctaButton: 'Zapytaj przez WhatsApp',
      feature1: 'Najlepsze oferty',
      feature2: 'Potwierdzona jakość',
      feature3: 'Wsparcie prawne',
    },
    featuresSection: {
      title: 'Dlaczego kupujący wybierają Villa.ooo',
    },
    villas: {
      label: 'Wille na sprzedaż',
      title: 'Luksusowe wille na sprzedaż w Chorwacji',
      viewAll: 'Zobacz wszystkie',
      bedrooms: 'sypialnie',
      bathrooms: 'łazienki',
      area: 'm²',
      details: 'Szczegóły',
      noVillas: 'Wille wkrótce',
    },
    contact: {
      label: 'Kontakt',
      title: 'Skontaktuj się z nami',
      description: 'Chcesz kupić willę w Chorwacji? Chętnie pomożemy!',
      name: 'Twoje imię',
      email: 'E-mail',
      phone: 'Telefon',
      message: 'Wiadomość',
      send: 'Wyślij zapytanie',
      success: 'Zapytanie wysłane!',
      successDesc: 'Skontaktujemy się wkrótce',
    },
    footer: {
      rights: 'Wszelkie prawa zastrzeżone',
      privacy: 'Polityka prywatności',
      seoText: 'Kupić willę w Chorwacji | Wille na sprzedaż w Chorwacji | Luksusowe nieruchomości nad morzem | Domy na sprzedaż w Dalmacji',
    },
    features: {
      bestOffers: 'Najlepsze oferty',
      bestOffersDesc: 'Oferujemy wille najwyższej jakości z naturalnych materiałów z najwyższą klasą energetyczną',
      quality: 'Potwierdzona jakość',
      qualityDesc: 'Otrzymujesz pełną dokumentację projektową dla pełnego zaufania do jakości',
      saveMoney: 'Oszczędź pieniądze',
      saveMoneyDesc: 'Kup willę w Chorwacji bezpośrednio od dewelopera po uczciwej cenie bez ukrytych opłat',
      bestPrice: 'Najlepsze wille w najlepszej cenie',
      bestPriceDesc: 'Szukasz luksusowych willi na sprzedaż w Chorwacji? Wszystkie nasze wille są w pierwszej linii brzegowej',
      legalSupport: 'Wsparcie prawne',
      legalSupportDesc: 'Gwarantujemy pełną przejrzystość i zgodność z przepisami UE',
      businessGain: 'Zysk biznesowy',
      businessGainDesc: 'Willę można wynajmować i uzyskiwać wysokie dochody',
    },
    catalog: {
      title: 'Katalog willi',
      subtitle: 'Znajdź willę swoich marzeń',
      filter: 'Filtry',
      sort: 'Sortuj',
      priceAsc: 'Cena: rosnąco',
      priceDesc: 'Cena: malejąco',
      all: 'Wszystkie wille',
      properties: 'nieruchomości',
    },
    villa: {
      features: 'Cechy',
      location: 'Lokalizacja',
      request: 'Zapytaj o ofertę',
      gallery: 'Galeria',
      back: 'Powrót do katalogu',
      bedrooms: 'Sypialnie',
      bathrooms: 'Łazienki',
      area: 'Powierzchnia mieszkalna',
      land: 'Powierzchnia działki',
      status: 'Status',
      forSale: 'Na sprzedaż',
      sold: 'Sprzedana',
      reserved: 'Zarezerwowana',
      priceOnRequest: 'Cena na zapytanie',
      comingSoon: 'Wkrótce',
      viewAllPhotos: 'Zobacz wszystkie zdjęcia',
      videoPresentation: 'Prezentacja wideo',
      notFound: 'Willa nie znaleziona',
    },
    admin: {
      login: 'Logowanie administratora',
      email: 'E-mail',
      password: 'Hasło',
      loginBtn: 'Zaloguj',
      logout: 'Wyloguj',
      dashboard: 'Panel',
      villas: 'Wille',
      contacts: 'Zapytania',
      addVilla: 'Dodaj willę',
      editVilla: 'Edytuj willę',
      deleteVilla: 'Usuń willę',
      save: 'Zapisz',
      cancel: 'Anuluj',
      published: 'Opublikowano',
      featured: 'Wyróżnione',
      noContacts: 'Brak zapytań',
      markRead: 'Oznacz jako przeczytane',
    },
    seo: {
      homeTitle: 'Villa.ooo — Premium wille w pierwszej linii brzegowej w Dalmacji, Chorwacja',
      homeDescription: 'Premium wille w Dalmacji, Chorwacja. Wille w pierwszej linii brzegowej. Bezpośrednio od dewelopera.',
      catalogTitle: 'Wille na sprzedaż w Chorwacji — Villa.ooo',
      catalogDescription: 'Luksusowe wille na sprzedaż w Dalmacji, Chorwacja. Trogir, Primosten, Omis, Makarska.',
    },
    dalmatia: {
      title: 'Dalmacja — Perła Chorwacji',
      description: 'Ucieknij na urokliwe wybrzeże dalmatyńskie Chorwacji — ukryty klejnot Europy.',
      region: 'Region',
      fullDescription: 'Dalmacja to region położony w południowej części Chorwacji, rozciągający się wzdłuż malowniczego wybrzeża Morza Adriatyckiego. Znana z oszałamiającej urody przyrody, Dalmacja oferuje unikalną kombinację wysp, krystalicznie czystych wód i bogatego dziedzictwa kulturowego.',
    },
    video: {
      title: 'Prezentacja wideo',
      subtitle: 'Zobacz nasze wille',
      description: 'Odkryj najpiękniejsze wille wysp dalmatyńskich — architektoniczne arcydzieła z panoramicznym widokiem na Morze Adriatyckie.',
    },
    cta: {
      title: 'Gotowy na własną willę marzeń?',
      description: 'Przeglądaj nasz ekskluzywny katalog willi lub skontaktuj się przez WhatsApp, aby uzyskać spersonalizowaną pomoc.',
      button: 'Przeglądaj wille',
      whatsapp: 'Rozmawiaj przez WhatsApp',
      contact: 'Zapytaj o szczegóły',
    },
  },
  sv: {
    nav: {
      home: 'Hem',
      catalog: 'Villor',
      contact: 'Kontakt',
      admin: 'Admin',
    },
    hero: {
      subtitle: 'Lyxvillor till salu i Kroatien',
      title: 'Köpa villa i Kroatien — Direkt vid havet',
      description: 'Vill du köpa en strandvilla i Kroatien? Vi erbjuder lyxvillor till salu direkt från byggherren — inga dolda avgifter. Hus till salu i Dalmatien.',
      cta: 'Se villor till salu',
      ctaSecondary: 'WhatsApp',
      imageAlt: 'Lyxvilla med infinitypool vid solnedgången i Kroatien',
    },
    images: {
      aboutAlt: 'Villor vid havet i Dalmatien, Kroatien',
      videoAlt: 'Lyxvillor i Dalmatien, Kroatien',
      dalmatiaAlt: 'Dalmatiens kust vid solnedgången',
      omisLabel: 'Omiš, Kroatien',
    },
    about: {
      label: 'Om oss',
      title: 'Köpa villa i Kroatien — Ditt drömhem väntar',
      description: 'Letar du efter hus till salu i Kroatien? Våra lyxvillor till salu ligger precis vid havet. Köp en havsnära villa i Kroatien direkt från byggherren. Strandvillor med exceptionell livsstil och stark investeringspotential.',
      details: 'Rena linjer, stora glaspartier och eleganta öppna planlösningar ramar in oavbruten havsutsikt. Glas från golv till tak fyller varje utrymme med naturligt ljus och skapar en sömlös koppling mellan raffinerat boende och skönheten vid vattnet.',
      ctaButton: 'Begär detaljer via WhatsApp',
      feature1: 'Bästa erbjudanden',
      feature2: 'Bekräftad kvalitet',
      feature3: 'Juridiskt stöd',
    },
    featuresSection: {
      title: 'Varför köpare väljer Villa.ooo',
    },
    villas: {
      label: 'Villor till salu',
      title: 'Lyxvillor till salu i Kroatien',
      viewAll: 'Visa alla',
      bedrooms: 'sovrum',
      bathrooms: 'badrum',
      area: 'm²',
      details: 'Detaljer',
      noVillas: 'Villor kommer snart',
    },
    contact: {
      label: 'Kontakt',
      title: 'Kontakta oss',
      description: 'Vill du köpa en villa i Kroatien? Vi hjälper dig gärna!',
      name: 'Ditt namn',
      email: 'E-post',
      phone: 'Telefon',
      message: 'Meddelande',
      send: 'Skicka förfrågan',
      success: 'Förfrågan skickad!',
      successDesc: 'Vi kontaktar dig snart',
    },
    footer: {
      rights: 'Alla rättigheter förbehållna',
      privacy: 'Integritetspolicy',
      seoText: 'Köpa villa i Kroatien | Villor till salu i Kroatien | Lyxiga strandfastigheter | Hus till salu i Dalmatien',
    },
    features: {
      bestOffers: 'Bästa erbjudanden',
      bestOffersDesc: 'Vi erbjuder villor av högsta kvalitet gjorda av naturliga material med högsta energieffektivitet',
      quality: 'Bekräftad kvalitet',
      qualityDesc: 'Du får fullständig projektdokumentation för fullt förtroende för kvaliteten',
      saveMoney: 'Spara pengar',
      saveMoneyDesc: 'Köp villa i Kroatien direkt från byggherren till ett rättvist pris utan dolda avgifter',
      bestPrice: 'Bästa villor till bästa pris',
      bestPriceDesc: 'Letar du efter lyxvillor till salu i Kroatien? Alla våra villor ligger i första linjen',
      legalSupport: 'Juridiskt stöd',
      legalSupportDesc: 'Vi garanterar absolut transparens och full överensstämmelse med EU-regler',
      businessGain: 'Affärsvinst',
      businessGainDesc: 'Villan kan hyras ut och ge hög inkomst',
    },
    catalog: {
      title: 'Villakatalog',
      subtitle: 'Hitta din drömvilla',
      filter: 'Filter',
      sort: 'Sortera',
      priceAsc: 'Pris: Lågt till högt',
      priceDesc: 'Pris: Högt till lågt',
      all: 'Alla villor',
      properties: 'fastigheter',
    },
    villa: {
      features: 'Funktioner',
      location: 'Plats',
      request: 'Begär info',
      gallery: 'Galleri',
      back: 'Tillbaka till katalog',
      bedrooms: 'Sovrum',
      bathrooms: 'Badrum',
      area: 'Boarea',
      land: 'Tomtarea',
      status: 'Status',
      forSale: 'Till salu',
      sold: 'Såld',
      reserved: 'Reserverad',
      priceOnRequest: 'Pris på förfrågan',
      comingSoon: 'Kommer snart',
      viewAllPhotos: 'Visa alla foton',
      videoPresentation: 'Videopresentation',
      notFound: 'Villa hittades inte',
    },
    admin: {
      login: 'Admin-inloggning',
      email: 'E-post',
      password: 'Lösenord',
      loginBtn: 'Logga in',
      logout: 'Logga ut',
      dashboard: 'Dashboard',
      villas: 'Villor',
      contacts: 'Förfrågningar',
      addVilla: 'Lägg till villa',
      editVilla: 'Redigera villa',
      deleteVilla: 'Ta bort villa',
      save: 'Spara',
      cancel: 'Avbryt',
      published: 'Publicerad',
      featured: 'Utvald',
      noContacts: 'Inga förfrågningar',
      markRead: 'Markera som läst',
    },
    seo: {
      homeTitle: 'Villa.ooo — Premium villor i första linjen i Dalmatien, Kroatien',
      homeDescription: 'Premium villor i Dalmatien, Kroatien. Villor i första linjen vid havet. Direkt från byggherren.',
      catalogTitle: 'Villor till salu i Kroatien — Villa.ooo',
      catalogDescription: 'Lyxvillor till salu i Dalmatien, Kroatien. Trogir, Primosten, Omis, Makarska.',
    },
    dalmatia: {
      title: 'Dalmatien — Kroatiens pärla',
      description: 'Fly till den förtrollande dalmatinska kusten i Kroatien — en dold juvel i Europa.',
      region: 'Region',
      fullDescription: 'Dalmatien är en region i södra Kroatien som sträcker sig längs den pittoreska kusten av Adriatiska havet. Känd för sin fantastiska naturskönhet erbjuder Dalmatien en unik kombination av öar, kristallklart vatten och rikt kulturarv.',
    },
    video: {
      title: 'Videopresentation',
      subtitle: 'Se våra villor',
      description: 'Upptäck de vackraste villorna på de dalmatinska öarna — arkitektoniska mästerverk med panoramautsikt över Adriatiska havet.',
    },
    cta: {
      title: 'Redo att äga din drömvilla?',
      description: 'Bläddra i vår exklusiva villakatalog eller kontakta oss via WhatsApp för personlig hjälp.',
      button: 'Bläddra villor',
      whatsapp: 'Prata via WhatsApp',
      contact: 'Begär detaljer',
    },
  },
  cs: {
    nav: {
      home: 'Domů',
      catalog: 'Vily',
      contact: 'Kontakt',
      admin: 'Admin',
    },
    hero: {
      subtitle: 'Luxusní vily na prodej v Chorvatsku',
      title: 'Koupit vilu v Chorvatsku — Přímo u moře',
      description: 'Chcete koupit vilu u moře v Chorvatsku? Nabízíme luxusní vily na prodej přímo od developera — žádné skryté poplatky. Domy na prodej v Dalmácii.',
      cta: 'Zobrazit vily na prodej',
      ctaSecondary: 'WhatsApp',
      imageAlt: 'Luxusní vila s infinity bazénem při západu slunce v Chorvatsku',
    },
    images: {
      aboutAlt: 'Vily u moře v Dalmácii, Chorvatsko',
      videoAlt: 'Luxusní vily v Dalmácii, Chorvatsko',
      dalmatiaAlt: 'Dalmatské pobřeží při západu slunce',
      omisLabel: 'Omiš, Chorvatsko',
    },
    about: {
      label: 'O nás',
      title: 'Koupit vilu v Chorvatsku — Váš vysněný dům čeká',
      description: 'Hledáte domy na prodej v Chorvatsku? Naše luxusní vily na prodej se nacházejí přímo u moře. Kupte vilu u moře v Chorvatsku přímo od developera. Plážové vily s výjimečným životním stylem a silným investičním potenciálem.',
      details: 'Čisté linie, rozlehlé prosklení a elegantní otevřené interiéry zarámují nerušený výhled na moře. Sklo od podlahy ke stropu naplní každý prostor přirozeným světlem a vytvoří bezešvé spojení mezi rafinovaným bydlením a krásou pobřeží.',
      ctaButton: 'Požádat o detaily přes WhatsApp',
      feature1: 'Nejlepší nabídky',
      feature2: 'Potvrzená kvalita',
      feature3: 'Právní podpora',
    },
    featuresSection: {
      title: 'Proč si kupující vybírají Villa.ooo',
    },
    villas: {
      label: 'Vily na prodej',
      title: 'Luxusní vily na prodej v Chorvatsku',
      viewAll: 'Zobrazit vše',
      bedrooms: 'ložnice',
      bathrooms: 'koupelny',
      area: 'm²',
      details: 'Detaily',
      noVillas: 'Vily brzy',
    },
    contact: {
      label: 'Kontakt',
      title: 'Kontaktujte nás',
      description: 'Chcete koupit vilu v Chorvatsku? Rádi vám pomůžeme!',
      name: 'Vaše jméno',
      email: 'E-mail',
      phone: 'Telefon',
      message: 'Zpráva',
      send: 'Odeslat poptávku',
      success: 'Poptávka odeslána!',
      successDesc: 'Brzy vás budeme kontaktovat',
    },
    footer: {
      rights: 'Všechna práva vyhrazena',
      privacy: 'Zásady ochrany osobních údajů',
      seoText: 'Koupit vilu v Chorvatsku | Vily na prodej v Chorvatsku | Luxusní přímořské nemovitosti | Domy na prodej v Dalmácii',
    },
    features: {
      bestOffers: 'Nejlepší nabídky',
      bestOffersDesc: 'Nabízíme vily nejvyšší kvality z přírodních materiálů s nejvyšší energetickou účinností',
      quality: 'Potvrzená kvalita',
      qualityDesc: 'Obdržíte kompletní projektovou dokumentaci pro plnou důvěru v kvalitu',
      saveMoney: 'Ušetřete peníze',
      saveMoneyDesc: 'Kupte vilu v Chorvatsku přímo od developera za férovou cenu bez skrytých poplatků',
      bestPrice: 'Nejlepší vily za nejlepší cenu',
      bestPriceDesc: 'Hledáte luxusní vily na prodej v Chorvatsku? Všechny naše vily jsou v první linii u moře',
      legalSupport: 'Právní podpora',
      legalSupportDesc: 'Garantujeme absolutní transparentnost a plný soulad s předpisy EU',
      businessGain: 'Obchodní zisk',
      businessGainDesc: 'Vilu lze pronajímat a získávat vysoký příjem',
    },
    catalog: {
      title: 'Katalog vil',
      subtitle: 'Najděte vilu svých snů',
      filter: 'Filtry',
      sort: 'Řadit',
      priceAsc: 'Cena: Od nejnižší',
      priceDesc: 'Cena: Od nejvyšší',
      all: 'Všechny vily',
      properties: 'nemovitostí',
    },
    villa: {
      features: 'Vlastnosti',
      location: 'Poloha',
      request: 'Požádat o info',
      gallery: 'Galerie',
      back: 'Zpět do katalogu',
      bedrooms: 'Ložnice',
      bathrooms: 'Koupelny',
      area: 'Obytná plocha',
      land: 'Pozemek',
      status: 'Status',
      forSale: 'Na prodej',
      sold: 'Prodáno',
      reserved: 'Rezervováno',
      priceOnRequest: 'Cena na vyžádání',
      comingSoon: 'Již brzy',
      viewAllPhotos: 'Zobrazit všechny fotky',
      videoPresentation: 'Videoprezentace',
      notFound: 'Vila nenalezena',
    },
    admin: {
      login: 'Přihlášení administrátora',
      email: 'E-mail',
      password: 'Heslo',
      loginBtn: 'Přihlásit',
      logout: 'Odhlásit',
      dashboard: 'Panel',
      villas: 'Vily',
      contacts: 'Poptávky',
      addVilla: 'Přidat vilu',
      editVilla: 'Upravit vilu',
      deleteVilla: 'Smazat vilu',
      save: 'Uložit',
      cancel: 'Zrušit',
      published: 'Publikováno',
      featured: 'Doporučené',
      noContacts: 'Žádné poptávky',
      markRead: 'Označit jako přečtené',
    },
    seo: {
      homeTitle: 'Villa.ooo — Prémiové vily v první linii v Dalmácii, Chorvatsko',
      homeDescription: 'Prémiové vily v Dalmácii, Chorvatsko. Vily v první linii u moře. Přímo od developera.',
      catalogTitle: 'Vily na prodej v Chorvatsku — Villa.ooo',
      catalogDescription: 'Luxusní vily na prodej v Dalmácii, Chorvatsko. Trogir, Primosten, Omis, Makarska.',
    },
    dalmatia: {
      title: 'Dalmácie — Perla Chorvatska',
      description: 'Unikněte na okouzlující dalmatské pobřeží Chorvatska — skrytý klenot Evropy.',
      region: 'Region',
      fullDescription: 'Dalmácie je region nacházející se v jižní části Chorvatska, táhnoucí se podél malebného pobřeží Jaderského moře. Známá svou ohromující přírodní krásou nabízí Dalmácie jedinečnou kombinaci ostrovů, křišťálově čisté vody a bohatého kulturního dědictví.',
    },
    video: {
      title: 'Videoprezentace',
      subtitle: 'Podívejte se na naše vily',
      description: 'Objevte nejkrásnější vily dalmatských ostrovů — architektonická mistrovská díla s panoramatickým výhledem na Jaderské moře.',
    },
    cta: {
      title: 'Připraveni vlastnit vilu svých snů?',
      description: 'Prohlédněte si náš exkluzivní katalog vil nebo nás kontaktujte přes WhatsApp pro osobní pomoc.',
      button: 'Prohlížet vily',
      whatsapp: 'Mluvte přes WhatsApp',
      contact: 'Požádat o detaily',
    },
  },
};

const languageNames = {
  en: 'English',
  de: 'Deutsch',
  pl: 'Polski',
  sv: 'Svenska',
  cs: 'Čeština',
};

const LanguageContext = createContext();

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState(() => {
    const saved = localStorage.getItem('villa-lang');
    return saved && translations[saved] ? saved : 'en';
  });

  useEffect(() => {
    localStorage.setItem('villa-lang', language);
    document.documentElement.lang = language;
  }, [language]);

  const t = (key) => {
    const keys = key.split('.');
    let value = translations[language];
    for (const k of keys) {
      value = value?.[k];
    }
    return value || key;
  };

  const availableLanguages = Object.keys(translations);

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t, availableLanguages, languageNames }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};
